import { delay, uuidv4 } from "./util";

const activeAnimations: Record<string, { el: HTMLElement, cancel: () => void }> = {};

/**
 *  allows animating from one style to another. Styles will not be reverted
 * 
 * @param el the element to apply the styles to
 * @param duration the duration in ms the animation should span
 * @param from the initial styles to set
 * @param to the styles to animate
 * 
 * @returns a promise that will trigger on completion. If another animation for this element is sent before this can complete, this promise will be canceled.
 */
export async function animate<T extends HTMLElement>(
    el: T,
    duration: number,
    from: Partial<CSSStyleDeclaration>,
    to: Partial<CSSStyleDeclaration>
): Promise<void> {
    return new Promise((resolve, reject) => {
        // Cancel any pending animations for this element
        for (const key in activeAnimations) {
            const anim = activeAnimations[key];
            if (anim.el === el) {
                anim.cancel();
                delete activeAnimations[key];
            }
        }

        const cleanup = () => {
            el.style.transition = "";
            el.style.overscrollBehavior = "";
        };

        // Register the cancellation method
        const animKey = uuidv4();
        activeAnimations[animKey] = {
            el: el,
            cancel: () => {
                reject();
                cleanup();
            }
        };

        // prevent scrollbars from activating
        el.style.overscrollBehavior = "contain";

        // apply initial styling
        for (const key in from) {
            const value = from[key as keyof typeof from];

            if (value !== undefined) {
                (el.style as any)[key] = value;
            }
        }

        // activate the transitions
        el.style.transition = Object.keys(to)
            .filter((k) => to[k as keyof typeof to] !== undefined)
            .map((k) => `${k} ${duration}ms linear`)
            .join(", ");

        // give the styles a chance to settle before triggering all the transitioned styles
        requestAnimationFrame(() => {
            for (const key in to) {
                const v = to[key as keyof typeof to];
                if (v != null) {
                    (el.style as any)[key] = v;
                }
            }
        });

        // wait for the transition to finish
        delay(duration)
            .then(() => {
                if (activeAnimations[animKey]) {
                    cleanup();
                    resolve();
                }
            });
    });
}
